import { FC, useMemo } from 'react';

import lang from '../../../lib/language';

import GoBackButton from '../../../components/GoBackButton/GoBackButton';
import Grid from '../../../components/Grid/Grid';
import LatestNews from '../../../components/News/LatestNews/LatestNews';
import Preloader from '../../../components/Preloader/Preloader';
import SymbolTable from '../../../components/SymbolTable/SymbolTable';
import { SymbolTableGrid } from '../../../components/SymbolTable/SymbolTableGrid.enum';
import useIsMobile from '../../hooks/useIsMobile';
import PopularGroupTableItem from '../components/PopularGroupTableItem/PopularGroupTableItem';
import { SYMBOL_TABLE_HEADERS } from '../PopularGroups.constants';
import { PopularGroupItemRow } from '../PopularGroups.types';

import { PopularGroupTableProps } from './PopularGroupTable.types';

import styles from './PopularGroupTable.module.scss';

const PRELOADER_STYLE = { height: '36px', width: '36px', margin: '0 auto' };

const PopularGroupTable: FC<PopularGroupTableProps> = ({
  rows,
  sector,
  symbolCount,
  isPending,
  isLoading,
  hasMorePages,
  showLoadingSpinner,
  enableRowToggle,
  titleRef,
  onLoadMore,
  onSymbolClick,
  onGoBack,
}: PopularGroupTableProps): JSX.Element => {
  const { isMobile } = useIsMobile();

  const tableHeaders = useMemo(() => (
    <div className={styles.tableHeaderRow}>
      {SYMBOL_TABLE_HEADERS.map((item: string) => (
        <div key={item} className={styles.tableHeaderCell}>
          <span>{item}</span>
        </div>
      ))}
    </div>
  ), []);

  const extraChildren = () => !isMobile && <div className={styles.wrapperSecondSection}><LatestNews /></div>;

  return (
    <Grid extraChildren={extraChildren()}>
      <div className={styles.wrapper}>
        <div className={styles.wrapperFirstSection}>
          {!isMobile && (
            <div className={styles.goBackButton}>
              <GoBackButton onBackBtnPress={onGoBack} />
            </div>
          )}
          <div className={styles.titleWrapper}>
            <div ref={titleRef} className={isMobile ? styles.titleMobile : styles.title}>
              {sector}
            </div>
            <div className={isMobile ? styles.stocksLabelMobile : styles.stocksLabel}>
              {`(${symbolCount} ${lang.accountBreakdownTableStockLabel()})`}
            </div>
          </div>
          <div className={styles.tableWrapper}>
            <SymbolTable
              items={rows}
              renderHeaders={() => tableHeaders}
              skeletonCount={rows.length}
              skeletonGrid={SymbolTableGrid.FIVE_ROW_ITEMS_GRID}
              isLoading={isLoading}
              renderRow={(row: PopularGroupItemRow) => (
                <PopularGroupTableItem
                  key={row.item.symbol}
                  row={row}
                  enableToggle={enableRowToggle}
                  onSymbolClick={onSymbolClick}
                />
              )}
            />
            {hasMorePages && (
              <div className={styles.viewMore}>
                <button
                  type="button"
                  onClick={onLoadMore}
                  className={styles.viewMoreButton}
                  disabled={isPending}
                >
                  {lang.commonLoadMore()}
                </button>
              </div>
            )}
            {isPending && showLoadingSpinner && <Preloader style={PRELOADER_STYLE} text="" />}
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default PopularGroupTable;
